// WebAuthn / Passkeys browser helper.
// Wraps navigator.credentials.create/get with the base64url ↔ ArrayBuffer
// conversions the backend expects.

function b64urlToBuf(b64url) {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
  const b64 = (b64url + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function bufToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function isPasskeySupported() {
  return (
    typeof window !== 'undefined' &&
    !!window.PublicKeyCredential &&
    typeof navigator !== 'undefined' &&
    !!navigator.credentials &&
    !!navigator.credentials.create &&
    !!navigator.credentials.get
  );
}

export async function isPlatformAuthenticatorAvailable() {
  try {
    if (!window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

function prepareCreateOptions(publicKey) {
  const options = { ...publicKey };
  options.challenge = b64urlToBuf(publicKey.challenge);
  options.user = { ...publicKey.user, id: b64urlToBuf(publicKey.user.id) };
  if (Array.isArray(publicKey.excludeCredentials)) {
    options.excludeCredentials = publicKey.excludeCredentials.map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
    }));
  }
  return options;
}

function prepareGetOptions(publicKey) {
  const options = { ...publicKey };
  options.challenge = b64urlToBuf(publicKey.challenge);
  if (Array.isArray(publicKey.allowCredentials)) {
    options.allowCredentials = publicKey.allowCredentials.map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
    }));
  }
  return options;
}

function attestationToJSON(cred) {
  const r = cred.response;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment || null,
    response: {
      clientDataJSON: bufToB64url(r.clientDataJSON),
      attestationObject: bufToB64url(r.attestationObject),
      transports:
        typeof r.getTransports === 'function' ? r.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
  };
}

function assertionToJSON(cred) {
  const r = cred.response;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment || null,
    response: {
      clientDataJSON: bufToB64url(r.clientDataJSON),
      authenticatorData: bufToB64url(r.authenticatorData),
      signature: bufToB64url(r.signature),
      userHandle: r.userHandle ? bufToB64url(r.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
  };
}

/**
 * Register a new passkey.
 * @param {{apiClient: any, basePath: string, nickname?: string}} args
 *  - apiClient: axios instance (already carries auth header)
 *  - basePath: '/auth/webauthn' or '/portal/webauthn'
 */
export async function registerPasskey({ apiClient, basePath, nickname = 'Passkey' }) {
  if (!isPasskeySupported()) throw new Error('Este navegador no soporta Passkeys');
  const optsResp = await apiClient.post(`${basePath}/register/options`, {});
  const publicKey = optsResp.data.publicKey;
  const challengeToken = optsResp.data.challenge_token;
  const options = prepareCreateOptions(publicKey);
  const cred = await navigator.credentials.create({ publicKey: options });
  if (!cred) throw new Error('Registro cancelado');
  const payload = attestationToJSON(cred);
  const verifyResp = await apiClient.post(`${basePath}/register/verify`, {
    challenge_token: challengeToken,
    credential: payload,
    nickname,
  });
  return verifyResp.data;
}

/**
 * Authenticate with a passkey.
 * @param {{apiClient: any, basePath: string, email?: string}} args
 */
export async function authenticateWithPasskey({ apiClient, basePath, email = '' }) {
  if (!isPasskeySupported()) throw new Error('Este navegador no soporta Passkeys');
  const optsResp = await apiClient.post(`${basePath}/login/options`, email ? { email } : {});
  const publicKey = optsResp.data.publicKey;
  const challengeToken = optsResp.data.challenge_token;
  const options = prepareGetOptions(publicKey);
  const cred = await navigator.credentials.get({ publicKey: options });
  if (!cred) throw new Error('Autenticación cancelada');
  const payload = assertionToJSON(cred);
  const verifyResp = await apiClient.post(`${basePath}/login/verify`, {
    challenge_token: challengeToken,
    credential: payload,
  });
  return verifyResp.data;
}
